import {
    ChangeDetectionStrategy,
    Component,
    OnInit
} from "@angular/core";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { Observable, combineLatest } from "rxjs";
import { filter, map, startWith, switchMap } from "rxjs/operators";

import {
    GetOrderForCheckoutQuery,
    GetNextOrderStatesQuery,
    TransitionToAddingItemsMutation,
} from "../../../common/generated-types";
import { DataService } from "../../../core/providers/data/data.service";
import { StateService } from "../../../core/providers/state/state.service";
import { GlobalService } from "../../../global.service";

import { orderHasStockIssues } from "../../../common/utils/cart-stock.utils";
import {
    GET_NEXT_ORDER_STATES,
    TRANSITION_TO_ADDING_ITEMS,
} from "./checkout-process.graphql";
import { BreadcrumbItem } from "../../../shared/components/breadcrumbs/breadcrumbs.component";

@Component({
    selector: "vsf-checkout-process",
    templateUrl: "./checkout-process.component.html",
    styleUrls: ["./checkout-process.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CheckoutProcessComponent implements OnInit {
    cart$: Observable<
        GetOrderForCheckoutQuery["activeOrder"] | null | undefined
    >;
    nextStates$: Observable<string[]>;
    activeStage$: Observable<number>;
    signedIn$: Observable<boolean>;
    shippingId$: Observable<string>;
    canProceedToPayment$: Observable<boolean>;
    hasPaymentMethod$: Observable<boolean>;
    shippingId = "";
    isOrderCompleted = false;
    breadcrumbItems: BreadcrumbItem[] = [];

    constructor(
        private dataService: DataService,
        private stateService: StateService,
        private route: ActivatedRoute,
        private router: Router,
        private globalService: GlobalService
    ) {}

    ngOnInit() {
        this.shippingId$ = this.globalService.shippingId$;

        this.shippingId$.subscribe((id) => {
            this.shippingId = id;
        });

        this.signedIn$ = this.stateService.select((state) => state.signedIn);
        this.cart$ = this.route.data.pipe(
            switchMap(
                (data) =>
                    data.activeOrder as Observable<
                        GetOrderForCheckoutQuery["activeOrder"]
                    >
            )
        );

        this.canProceedToPayment$ = combineLatest([
            this.shippingId$,
            this.cart$,
        ]).pipe(
            map(([shippingId, cart]) => {
                const hasShippingId = !!shippingId;
                const hasShippingAddress = !!cart?.shippingAddress?.streetLine1;
                const stockOk = !orderHasStockIssues(cart);
                return hasShippingId && hasShippingAddress && stockOk;
            })
        );

        this.hasPaymentMethod$ = this.globalService.selectedPaymentMethod$.pipe(
            map((method) => !!method)
        );

        this.nextStates$ = this.dataService
            .query<GetNextOrderStatesQuery>(GET_NEXT_ORDER_STATES)
            .pipe(map((data) => data.nextOrderStates));
        this.activeStage$ = this.router.events.pipe(
            filter((event) => event instanceof NavigationEnd),
            startWith(true),
            map(() => {
                const firstChild = this.route.snapshot.firstChild;
                if (firstChild && firstChild.routeConfig) {
                    switch (firstChild.routeConfig.path) {
                        case "":
                            return 1;
                        case "shipping":
                            return 2;
                        case "payment":
                            return 3;
                        case "confirmation/:code":
                            return 4;
                    }
                }
                return 1;
            })
        );

        this.router.events.subscribe(() => {
            this.isOrderCompleted = this.router.url.includes("confirmation");
            this.updateBreadcrumbs();
        });
        this.activeStage$.subscribe(() => this.updateBreadcrumbs());
    }

    private updateBreadcrumbs() {
        this.breadcrumbItems = [
            { label: 'Checkout', isActive: false },
            { label: 'Address & Payment', isActive: !this.isOrderCompleted },
            { label: 'Complete', isActive: this.isOrderCompleted }
        ];
    }

    triggerProceedToPayment() {
        this.globalService.triggerProceedToPayment();
    }

    changeShippingAddress() {
        this.dataService
            .mutate<TransitionToAddingItemsMutation>(TRANSITION_TO_ADDING_ITEMS)
            .subscribe(() => {
                this.router.navigate(["./shipping"], {
                    relativeTo: this.route,
                });
            });
    }
}
